const API_BASE = '/api'

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    ...options,
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.error || `Request failed: ${res.status}`)
  }

  return res.json()
}

export const api = {
  // Auth
  signup: (data: { email: string; password: string; firstName?: string; lastName?: string; displayName?: string }) =>
    request<{ user: any }>('/auth/signup', { method: 'POST', body: JSON.stringify(data) }),

  login: (email: string, password: string) =>
    request<{ user: any }>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),

  logout: () =>
    request<{ success: boolean }>('/auth/logout', { method: 'POST' }),

  getMe: () =>
    request<{ user: any }>('/auth/me'),

  // Profile
  getProfile: () =>
    request<{ profile: any; avatar: any }>('/user/profile'),

  updateProfile: (data: any) =>
    request<{ profile: any }>('/user/profile', { method: 'PATCH', body: JSON.stringify(data) }),

  // Gamification
  getStats: () =>
    request<{ stats: any; avatar: any }>('/gamification/stats'),

  getAchievements: () =>
    request<{ achievements: any[] }>('/gamification/achievements'),

  getLeaderboard: (limit = 20) =>
    request<{ leaderboard: any[] }>(`/gamification/leaderboard?limit=${limit}`),

  getQuests: (type?: string) =>
    request<{ quests: any[] }>(`/gamification/quests${type ? `?type=${type}` : ''}`),

  // Workouts
  getPrograms: () =>
    request<{ programs: any[] }>('/workouts/programs'),

  getProgram: (slug: string) =>
    request<{ program: any }>(`/workouts/programs/${slug}`),

  logWorkout: (data: any) =>
    request<{ log: any; xp: any }>('/workouts/log', { method: 'POST', body: JSON.stringify(data) }),

  getWorkoutHistory: (limit = 20, offset = 0) =>
    request<{ logs: any[]; total: number }>(`/workouts/history?limit=${limit}&offset=${offset}`),

  // Progress
  logProgress: (data: any) =>
    request<{ log: any }>('/progress/log', { method: 'POST', body: JSON.stringify(data) }),

  getProgressHistory: (limit = 50) =>
    request<{ logs: any[] }>(`/progress/history?limit=${limit}`),

  getProgressSummary: () =>
    request<{ earliest: any; latest: any; totalLogs: number }>('/progress/summary'),

  // Classes
  getClasses: () =>
    request<{ classes: any[] }>('/classes'),

  bookClass: (classId: string) =>
    request<{ booking: any }>(`/classes/${classId}/book`, { method: 'POST' }),

  cancelBooking: (classId: string) =>
    request<{ success: boolean }>(`/classes/${classId}/book`, { method: 'DELETE' }),

  getMyBookings: () =>
    request<{ bookings: any[] }>('/classes/my-bookings'),

  // Survey
  submitSurvey: (data: any) =>
    request<{ response: any; recommendation: any }>('/survey', { method: 'POST', body: JSON.stringify(data) }),

  // Billing
  createCheckout: (plan: string) =>
    request<{ url: string }>('/billing/create-checkout', { method: 'POST', body: JSON.stringify({ plan }) }),

  getSubscription: () =>
    request<{ subscription: any }>('/billing/subscription'),

  createPortalSession: () =>
    request<{ url: string }>('/billing/portal', { method: 'POST' }),
}
